import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Store, Trash2, AlertCircle, Eye, ExternalLink, Euro, TrendingUp, Wallet, Download, CheckCircle2, ShieldAlert, Loader2, BellRing, History } from 'lucide-react';
import { toast } from 'sonner';
import AdminReportsAuditPanel from '../components/AdminReportsAuditPanel';

const fmtEur = (cents) => `${((cents || 0) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const AdminShops = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('shops'); // shops | claims | reports | finance | deletion_requests | siret
  const [shops, setShops] = useState([]);
  const [claims, setClaims] = useState([]);
  const [reports, setReports] = useState([]);
  const [finance, setFinance] = useState(null);
  const [deletionRequests, setDeletionRequests] = useState([]);
  const [auditReportId, setAuditReportId] = useState(null);

  useEffect(() => { loadAll(); }, []);
  const loadAll = async () => {
    try {
      const [s, c, r, f, dr] = await Promise.all([
        axios.get('/api/shop/admin/all'),
        axios.get('/api/shop/admin/claims'),
        axios.get('/api/shop/admin/reports'),
        axios.get('/api/shop/admin/finance-summary'),
        axios.get('/api/shop/admin/deletion-requests'),
      ]);
      setShops(s.data?.shops || []);
      setClaims(c.data?.claims || []);
      setReports(r.data?.reports || []);
      setFinance(f.data || null);
      setDeletionRequests(dr.data?.requests || []);
    } catch { toast.error('Accès admin requis'); }
  };

  const approveDeletion = async (id) => {
    if (!window.confirm('Confirmer la suppression définitive de cette boutique ?')) return;
    await axios.post(`/api/shop/admin/shops/${id}/approve-deletion`);
    toast.success('Boutique supprimée — le PRO a été notifié');
    loadAll();
  };
  const rejectDeletion = async (id) => {
    if (!window.confirm('Refuser la demande ? La boutique réapparaîtra dans Destine Ads du PRO.')) return;
    await axios.post(`/api/shop/admin/shops/${id}/reject-deletion`);
    toast.success('Demande refusée — boutique restaurée pour le PRO');
    loadAll();
  };

  const resolveReport = async (id) => {
    await axios.patch(`/api/shop/admin/reports/${id}`);
    toast.success('Signalement résolu');
    loadAll();
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer définitivement cette boutique ?')) return;
    await axios.delete(`/api/shop/admin/shops/${id}`);
    toast.success('Boutique supprimée');
    loadAll();
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-shops">
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-md border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-lg font-bold flex items-center gap-2"><Store className="w-5 h-5 text-fuchsia-400" /> Admin · MyShop</h1>
      </header>

      {/* Global finance banner — visible across all tabs */}
      {finance && (
        <section className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-fuchsia-900/10 to-violet-900/10" data-testid="admin-shops-finance-banner">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
              <p className="text-[10px] text-gray-400 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Brut total</p>
              <p className="text-base font-bold text-white" data-testid="admin-finance-gross">{fmtEur(finance.totals?.gross_cents)}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-fuchsia-500/15 border border-fuchsia-500/30">
              <p className="text-[10px] text-fuchsia-200/80 flex items-center gap-1"><Euro className="w-3 h-3" /> Commission Destine ({finance.commission_percent || 5}%)</p>
              <p className="text-base font-bold text-fuchsia-200" data-testid="admin-finance-commission">{fmtEur(finance.totals?.commission_cents)}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-emerald-500/15 border border-emerald-500/40">
              <p className="text-[10px] text-emerald-200/80 flex items-center gap-1"><Wallet className="w-3 h-3" /> À reverser aux vendeurs</p>
              <p className="text-base font-bold text-emerald-200" data-testid="admin-finance-net">{fmtEur(finance.totals?.net_to_pay_cents)}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
              <p className="text-[10px] text-gray-400">Ventes / boutiques actives</p>
              <p className="text-base font-bold text-white">{finance.totals?.sales_count || 0} <span className="text-xs text-gray-400">/ {finance.totals?.shops_count || 0}</span></p>
            </div>
          </div>
        </section>
      )}

      <nav className="flex border-b border-white/10 px-2 overflow-x-auto">
        <button onClick={() => setTab('shops')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === 'shops' ? 'text-fuchsia-300 border-fuchsia-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-shops">Boutiques ({shops.length})</button>
        <button onClick={() => setTab('finance')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === 'finance' ? 'text-emerald-300 border-emerald-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-finance">Versements ({finance?.shops?.length || 0})</button>
        <button onClick={() => setTab('claims')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === 'claims' ? 'text-fuchsia-300 border-fuchsia-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-claims">Réclamations ({claims.length})</button>
        <button onClick={() => setTab('reports')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === 'reports' ? 'text-red-300 border-red-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-reports">
          Signalements ({reports.filter(r => r.status === 'open').length})
        </button>
        <button onClick={() => setTab('deletion_requests')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap flex items-center gap-1.5 ${tab === 'deletion_requests' ? 'text-amber-300 border-amber-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-deletion-requests">
          <BellRing className="w-3.5 h-3.5" />
          Demandes de suppression ({deletionRequests.length})
        </button>
        <button onClick={() => setTab('siret')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap ${tab === 'siret' ? 'text-fuchsia-300 border-fuchsia-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-siret">
          Vérif. SIRET (INSEE)
        </button>
        <button onClick={() => setTab('myfood')} className={`px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap flex items-center gap-1.5 ${tab === 'myfood' ? 'text-emerald-300 border-emerald-500' : 'text-gray-400 border-transparent'}`} data-testid="admin-shops-tab-myfood">
          MyFood Docs
        </button>
      </nav>
      <main className="px-4 py-4 space-y-2">
        {tab === 'siret' && <SiretSearchPanel />}
        {tab === 'myfood' && <AdminMyFoodDocs />}
        {tab === 'deletion_requests' && (
          deletionRequests.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="deletion-requests-empty">
              <BellRing className="w-10 h-10 mx-auto mb-2 text-gray-500" />
              <p className="text-sm">Aucune demande de suppression en attente.</p>
              <p className="text-xs mt-1">Les PRO qui demandent la suppression de leur boutique depuis Destine Ads apparaîtront ici.</p>
            </div>
          ) : (
            deletionRequests.map(s => {
              const askedAt = (() => { try { return new Date(s.deletion_requested_at).toLocaleString('fr-FR'); } catch { return s.deletion_requested_at; } })();
              return (
                <div key={s.id} className="p-3 rounded-2xl bg-amber-500/10 border border-amber-500/30 space-y-2" data-testid={`deletion-request-${s.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                      <BellRing className="w-5 h-5 text-amber-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-amber-100 truncate">{s.name}</p>
                      <p className="text-[11px] text-amber-200/80">PRO : {s.owner?.name || '—'} · {s.owner?.email || ''}</p>
                      <p className="text-[11px] text-gray-400">Demandée le {askedAt}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <button onClick={() => navigate(`/shop/${s.id}`)} className="flex-1 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`deletion-view-${s.id}`}>
                      <Eye className="w-3 h-3" /> Voir la boutique
                    </button>
                    <button onClick={() => approveDeletion(s.id)} className="flex-1 py-1.5 rounded-full bg-red-600 hover:bg-red-700 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`deletion-approve-${s.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Approuver
                    </button>
                    <button onClick={() => rejectDeletion(s.id)} className="flex-1 py-1.5 rounded-full bg-emerald-600/50 hover:bg-emerald-600 text-[11px] font-semibold flex items-center justify-center gap-1" data-testid={`deletion-reject-${s.id}`}>
                      <Trash2 className="w-3 h-3 rotate-90" /> Refuser
                    </button>
                  </div>
                </div>
              );
            })
          )
        )}
        {tab === 'shops' && shops.map(s => (
          <div key={s.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`admin-shop-${s.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 rounded-xl bg-white/5 overflow-hidden shrink-0">
                {s.cover_url ? <img src={s.cover_url.startsWith('http') ? s.cover_url : s.cover_url} alt="" className="w-full h-full object-cover" /> : <Store className="w-8 h-8 m-4 text-gray-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{s.name}</p>
                <p className="text-xs text-gray-400">{s.owner?.name || '—'} · {s.country} · {s.category}</p>
                <p className="text-xs text-gray-400">SIRET: {s.siret}</p>
                <p className="text-xs text-emerald-300 mt-1">{s.sales_count} ventes · Net cumulé : {fmtEur(s.revenue_cents)} ({(s.currency || 'eur').toUpperCase()})</p>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => navigate(`/shop/${s.id}`)} className="p-2 rounded-full bg-white/10" data-testid={`admin-shop-view-${s.id}`}><Eye className="w-4 h-4" /></button>
                <button onClick={() => del(s.id)} className="p-2 rounded-full bg-red-500/20 text-red-300" data-testid={`admin-shop-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <SiretVerifyPanel siret={s.siret} />
            <PayoutSnippet userId={s.owner_id} />
          </div>
        ))}
        {tab === 'finance' && (
          <>
            <div className="flex justify-end mb-2">
              <button
                onClick={async () => {
                  try {
                    const res = await axios.get('/api/shop/admin/finance-summary/export.csv', { responseType: 'blob' });
                    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `destinestudio_eshop_payouts_${new Date().toISOString().slice(0, 10)}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                  } catch { toast.error('Erreur export CSV'); }
                }}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-full text-xs font-semibold flex items-center gap-1.5"
                data-testid="admin-finance-export-csv"
              >
                <Download className="w-4 h-4" /> Exporter CSV
              </button>
            </div>
            {(finance?.shops || []).length === 0
            ? <p className="text-center text-sm text-gray-400 py-8">Aucune vente enregistrée pour l'instant.</p>
            : (finance?.shops || []).map(row => (
              <div key={row.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`admin-finance-row-${row.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <p className="font-bold truncate">{row.name}</p>
                    <p className="text-[11px] text-gray-400 truncate">{row.owner_name || '—'} · {row.owner_email || '—'}</p>
                  </div>
                  <button onClick={() => navigate(`/shop/${row.id}`)} className="p-2 rounded-full bg-white/10 shrink-0"><Eye className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-white/5">
                    <p className="text-[10px] text-gray-400">Brut</p>
                    <p className="text-sm font-bold text-white">{fmtEur(row.gross_cents)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-fuchsia-500/10">
                    <p className="text-[10px] text-fuchsia-200/80">Commission</p>
                    <p className="text-sm font-bold text-fuchsia-200">−{fmtEur(row.commission_cents)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-emerald-500/15">
                    <p className="text-[10px] text-emerald-200/80">Net à reverser</p>
                    <p className="text-sm font-bold text-emerald-200" data-testid={`admin-finance-row-net-${row.id}`}>{fmtEur(row.net_cents)}</p>
                  </div>
                </div>
                <div className="mt-2 p-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 text-[11px] space-y-0.5">
                  <p><span className="text-gray-400">Raison sociale :</span> <span className="font-semibold">{row.company_name || '—'}</span></p>
                  <p className="font-mono"><span className="text-gray-400">IBAN :</span> {row.iban || '—'}</p>
                  <p className="font-mono"><span className="text-gray-400">BIC :</span> {row.bic || '—'}</p>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">{row.sales_count} vente(s)</p>
              </div>
            ))}
          </>
        )}
        {tab === 'claims' && (
          claims.length === 0
            ? <p className="text-center text-sm text-gray-400 py-8">Aucune réclamation</p>
            : claims.map(c => (
          <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`admin-claim-${c.id}`}>
            <p className="text-sm font-semibold flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-400" /> {c.reason}</p>
            <p className="text-xs text-gray-300 mt-1">{c.message}</p>
            {(c.shop_name || c.buyer?.name) && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                {c.shop_name && <span>Boutique : <span className="font-semibold text-white">{c.shop_name}</span></span>}
                {c.buyer?.name && <span>· Acheteur : <span className="text-white">{c.buyer.name}</span></span>}
                {c.buyer?.email && <span className="text-gray-500 truncate max-w-[160px]">{c.buyer.email}</span>}
              </div>
            )}
            <div className="mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded-full text-[10px] ${c.status === 'open' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{c.status}</span>
              {c.seller_response && <span className="text-[10px] text-emerald-300 italic">Vendeur a répondu</span>}
              <button onClick={() => navigate(`/shop/${c.shop_id}`)} className="ml-auto px-2 py-1 rounded-full bg-white/10 text-[10px] flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Boutique</button>
            </div>
          </div>
        )))}
        {tab === 'reports' && (
          <>
            <AdminReportsAuditPanel
              kind="shop"
              selectedReportId={auditReportId}
              onCloseSelected={() => setAuditReportId(null)}
            />
            {reports.length === 0 ? <p className="text-center text-sm text-gray-400 py-8">Aucun signalement</p> :
            reports.map(r => (
              <div key={r.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`admin-report-${r.id}`}>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" /> {r.reason}
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] ${r.status === 'open' ? 'bg-red-500/20 text-red-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{r.status}</span>
                </p>
                {r.message && <p className="text-xs text-gray-300 mt-1 italic">« {r.message} »</p>}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-400">
                  <span>Boutique : <span className="font-semibold text-white">{r.shop_name}</span></span>
                  <span>·</span>
                  <span>Par : {r.reporter?.name || r.reporter?.username || '—'}</span>
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <button onClick={() => setAuditReportId(r.id)} className="px-3 py-1.5 rounded-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 text-xs flex items-center justify-center gap-1" data-testid={`open-history-${r.id}`}><History className="w-3 h-3" /> Historique</button>
                  <button onClick={() => navigate(`/shop/${r.shop_id}`)} className="flex-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs flex items-center justify-center gap-1"><ExternalLink className="w-3 h-3" /> Voir boutique</button>
                  {r.status === 'open' && (
                    <button onClick={() => resolveReport(r.id)} className="flex-1 px-3 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold" data-testid={`resolve-report-${r.id}`}>Marquer résolu</button>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
};

// Lazy-loads the seller's bank info per shop card.
const PayoutSnippet = ({ userId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`/api/shop/admin/payout-info/${userId}`).then(r => setData(r.data || {})).catch(() => setData({}));
  }, [userId]);
  if (!data) return null;
  return (
    <div className="mt-2 p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-[11px]">
      <p className="font-semibold text-emerald-200">💳 RIB</p>
      <p><span className="text-gray-400">Raison sociale :</span> {data.company_name || '—'}</p>
      <p className="font-mono"><span className="text-gray-400">IBAN :</span> {data.iban || '—'}</p>
      <p className="font-mono"><span className="text-gray-400">BIC :</span> {data.bic || '—'}</p>
    </div>
  );
};

// Admin can verify a SIRET against the INSEE Sirene database.
const SiretVerifyPanel = ({ siret }) => {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const verify = async () => {
    if (!siret) return;
    setState({ loading: true, error: null, data: null });
    try {
      const r = await axios.get(`/api/shop/admin/verify-siret/${encodeURIComponent(siret)}`);
      setState({ loading: false, error: null, data: r.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Erreur INSEE', data: null });
    }
  };
  return (
    <div className="mt-2 p-2 rounded-xl bg-white/5 border border-white/10 text-[11px]" data-testid={`siret-verify-${siret}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="font-semibold text-fuchsia-200 flex items-center gap-1">
          <ShieldAlert className="w-3.5 h-3.5" /> Vérification INSEE Sirene
        </p>
        <button
          onClick={verify}
          disabled={state.loading || !siret}
          className="px-2.5 py-1 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-[10px] font-semibold flex items-center gap-1"
          data-testid={`siret-verify-btn-${siret}`}
        >
          {state.loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
          Vérifier
        </button>
      </div>
      {state.error && <p className="text-red-300 mt-1">{state.error}</p>}
      {state.data && state.data.found && (
        <div className="mt-1 space-y-0.5" data-testid={`siret-verify-result-${siret}`}>
          <p><span className="text-gray-400">Raison sociale :</span> <span className="font-semibold text-white">{state.data.company_name || '—'}</span></p>
          <p><span className="text-gray-400">SIRET INSEE :</span> <span className="font-mono">{state.data.siret || state.data.siren}</span></p>
          {state.data.naf_code && <p><span className="text-gray-400">Code NAF :</span> {state.data.naf_code}</p>}
          {state.data.address && <p><span className="text-gray-400">Adresse :</span> {state.data.address}</p>}
          {state.data.created_at && <p><span className="text-gray-400">Date création :</span> {state.data.created_at}</p>}
          <p>
            <span className="text-gray-400">Statut :</span>{' '}
            {state.data.is_active
              ? <span className="text-emerald-300 font-semibold">Actif ✓</span>
              : <span className="text-red-300 font-semibold">Cessé / inactif</span>}
          </p>
        </div>
      )}
      {state.data && state.data.found === false && (
        <p className="text-red-300 mt-1">Aucun établissement trouvé dans la base Sirene.</p>
      )}
    </div>
  );
};


// Standalone INSEE Sirene search panel (admin tab)
// Lets admins paste any SIRET (14 digits) or SIREN (9 digits) and instantly
// verify the company against the official INSEE Sirene database, without
// needing the shop to exist on Destine yet.
const SiretSearchPanel = () => {
  const [siret, setSiret] = useState('');
  const [state, setState] = useState({ loading: false, error: null, data: null });

  const verify = async () => {
    const cleaned = siret.replace(/\s/g, '');
    if (!cleaned || ![9, 14].includes(cleaned.replace(/\D/g, '').length)) {
      setState({ loading: false, error: 'Entrez un SIRET (14 chiffres) ou un SIREN (9 chiffres).', data: null });
      return;
    }
    setState({ loading: true, error: null, data: null });
    try {
      const r = await axios.get(`/api/shop/admin/verify-siret/${encodeURIComponent(cleaned)}`);
      setState({ loading: false, error: null, data: r.data });
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Erreur INSEE', data: null });
    }
  };

  return (
    <div className="space-y-3" data-testid="siret-search-panel">
      <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
        <p className="text-sm font-semibold mb-2 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-fuchsia-300" />
          Vérifier une entreprise (INSEE Sirene)
        </p>
        <p className="text-xs text-gray-400 mb-3">
          Saisissez le numéro SIRET (14 chiffres) ou SIREN (9 chiffres) d'une entreprise — la raison sociale, l'adresse et le statut sont rapatriés en temps réel depuis la base officielle de l'INSEE.
        </p>
        <div className="flex gap-2">
          <input
            value={siret}
            onChange={e => setSiret(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') verify(); }}
            placeholder="Ex : 35600000000048"
            inputMode="numeric"
            className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm font-mono focus:outline-none focus:border-fuchsia-500"
            data-testid="siret-search-input"
          />
          <button
            onClick={verify}
            disabled={state.loading}
            className="px-4 py-2 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 disabled:opacity-50 text-sm font-semibold flex items-center gap-1.5"
            data-testid="siret-search-submit"
          >
            {state.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Vérifier
          </button>
        </div>
        {state.error && <p className="text-red-300 text-sm mt-2" data-testid="siret-search-error">{state.error}</p>}
      </div>

      {state.data && state.data.found && (
        <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/30" data-testid="siret-search-result">
          <p className="text-base font-bold text-emerald-200 mb-2 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            {state.data.company_name || '—'}
          </p>
          <div className="space-y-1 text-xs">
            <p><span className="text-gray-400">SIRET :</span> <span className="font-mono">{state.data.siret || state.data.siren}</span></p>
            {state.data.siret && state.data.siren && (
              <p><span className="text-gray-400">SIREN :</span> <span className="font-mono">{state.data.siren}</span></p>
            )}
            {state.data.naf_code && <p><span className="text-gray-400">Code NAF :</span> {state.data.naf_code}</p>}
            {state.data.address && <p><span className="text-gray-400">Adresse :</span> {state.data.address}</p>}
            {state.data.created_at && <p><span className="text-gray-400">Date de création :</span> {state.data.created_at}</p>}
            <p>
              <span className="text-gray-400">Statut :</span>{' '}
              {state.data.is_active
                ? <span className="text-emerald-300 font-semibold">Actif ✓</span>
                : <span className="text-red-300 font-semibold">Cessé / inactif</span>}
            </p>
          </div>
        </div>
      )}
      {state.data && state.data.found === false && (
        <div className="p-3 rounded-2xl bg-red-500/10 border border-red-500/30">
          <p className="text-sm text-red-300">Aucun établissement trouvé dans la base Sirene pour ce numéro.</p>
        </div>
      )}
    </div>
  );
};

// v49.4 — MyFood documents admin view (4 languages for the admin console)
const ADMIN_DOC_L = {
  fr: { title: 'Documents MyFood (restaurants)', empty: 'Aucun document', by: 'Boutique', open: 'Ouvrir', delete: 'Supprimer', confirmDel: 'Supprimer ce document ?', kind_permit: "Permis d'exploitation", kind_hygiene: 'Hygiène alimentaire', kind_other: 'Autre' },
  en: { title: 'MyFood documents (restaurants)', empty: 'No documents', by: 'Shop', open: 'Open', delete: 'Delete', confirmDel: 'Delete this document?', kind_permit: 'Business permit', kind_hygiene: 'Food hygiene', kind_other: 'Other' },
  es: { title: 'Documentos MyFood (restaurantes)', empty: 'Sin documentos', by: 'Tienda', open: 'Abrir', delete: 'Eliminar', confirmDel: '¿Eliminar este documento?', kind_permit: 'Permiso', kind_hygiene: 'Higiene alimentaria', kind_other: 'Otro' },
  ht: { title: 'Dokiman MyFood (restoran)', empty: 'Pa gen dokiman', by: 'Boutik', open: 'Louvri', delete: 'Efase', confirmDel: 'Efase dokiman sa a?', kind_permit: 'Pèmi', kind_hygiene: 'Ijyèn alimantè', kind_other: 'Lòt' },
};

const AdminMyFoodDocs = () => {
  const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('destine_language')) || 'fr';
  const l = ADMIN_DOC_L[lang] || ADMIN_DOC_L.fr;
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/shop/admin/shops/documents?category=myfood');
      setRows(r.data.shops || []);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  React.useEffect(() => { load(); }, []);
  const del = async (shopId, docId) => {
    if (!window.confirm(l.confirmDel)) return;
    try {
      await axios.delete(`/api/shop/admin/shops/${shopId}/documents/${docId}`);
      toast.success('OK');
      load();
    } catch { toast.error('Erreur'); }
  };
  if (loading) return <Loader2 className="w-6 h-6 animate-spin mx-auto" />;
  if (rows.length === 0) return <p className="text-center text-gray-400 py-8">{l.empty}</p>;
  return (
    <div className="space-y-3" data-testid="admin-myfood-docs">
      <h3 className="text-sm font-bold flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-emerald-300" /> {l.title}</h3>
      {rows.map(s => (
        <div key={s.id} className="p-3 rounded-2xl bg-white/5 border border-white/10">
          <p className="text-sm font-semibold mb-2">{l.by} : {s.name} <span className="text-xs text-white/40">({s.id.slice(0, 8)})</span></p>
          <div className="space-y-1.5">
            {(s.documents || []).map(d => (
              <div key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/5 text-xs">
                <span className="flex-1 min-w-0 truncate">
                  <span className="font-semibold">{d.title}</span>
                  <span className="ml-2 text-white/50">· {l[`kind_${d.kind}`] || d.kind}</span>
                </span>
                <a href={d.url} target="_blank" rel="noreferrer" className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30" data-testid={`admin-myfood-open-${d.id}`}>{l.open}</a>
                <button onClick={() => del(s.id, d.id)} className="px-2 py-0.5 rounded bg-red-500/20 text-red-200 hover:bg-red-500/30" data-testid={`admin-myfood-del-${d.id}`}>{l.delete}</button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminShops;

